import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { ColumnMinWidth, FILE_TYPE_FILTER_OPTIONS, getFileTypeFromName, OverrideColumnDefinition, OverrideColumnParameters, SearchGridOptionsFormatter } from './search-grid-options-formatter';
import { BytesFilter } from '@app/core/utilities/filters/bytes.filter';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import IResource from '@app/core/model/resource';
import { ISearchResourceConfig } from '@app/core/services/resource-dictionary/search-resource-dictionary-types';
import { DataTableUtilities } from '@app/core/utilities/data-table-utilities';
import { BsshDatePipe } from '@app/core/utilities/pipes/bssh-date.pipe';
import { FilePreviewCellRendererComponent } from '@app/shared/cell-renderers/file-preview-cell-renderer/file-preview-cell-renderer.component';
import { Observable, of } from 'rxjs';
import { FilterOption } from '@bssh/comp-lib/lib/components/data-table/custom-colDef';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { RadLinkRendererComponent } from '@app/shared/cell-renderers/rad-link-renderer/rad-link-renderer.component';
import { FastqQcStatus } from '@app/core/model/datasets/fastq-qc-status';
import { StatusToDisplayName } from '@app/core/utilities/pipes/status-to-display-name.pipe';
import { StringUtilities } from '@app/core/utilities/string-utilities';
import _ from 'lodash';

export enum FileSearchColumnFields {
    Name = 'Name',
    Path = 'Path',
    FileType = 'FileType',
    Analysis = 'Analysis',
    Dataset = 'Dataset',
    Project = 'Project',
    Size = 'Size',
    Created = 'Created',
    DatasetStatus = 'DatasetStatus'
}


export class FileSearchGridOptionsFormatter extends SearchGridOptionsFormatter<FileSearchColumnFields> {
    private fileResourceConfig: ISearchResourceConfig;
    public projectFilterOptions$: Observable<FilterOption[]>;
    public projectColumnFilterOptionsUpdateCallback;

    constructor(
        public resourceDictionaryService: SearchResourceDictionaryService,
        public bsshDatePipe: BsshDatePipe,
        public http: HttpClient,
        public statusToDisplayName: StatusToDisplayName
    ) {
        super(resourceDictionaryService, SearchResourceType.File);
        this.fileResourceConfig = this.resourceDictionaryService.getResourceConfigByType(SearchResourceType.File);
        this.bindFilterUpdateCallbacksToCurrentContext();
        this.projectFilterOptions$ = this.updateProjectColumnFilterOptions();
    }

    getProjectFilterOptions(arg?: string): Observable<any> {
        return this.http.get(BsApiEndPoints.SEARCH_FILTER_URL_V1PRE3, {
            params: {
                limit: '50',
                scope: 'projects',
                sortBy: 'ModifiedOn',
                sortDir: 'desc',
                query: arg ? `Project.Name:"${arg}"` : ''
            },
            withCredentials: true
        });
    }

    updateProjectColumnFilterOptions(arg?: string): Observable<FilterOption[]> {
        return this.getProjectFilterOptions(arg).pipe(
          map((response) => {
            return this.toProjectFilterOptions(response);
          })
        );
    }

    bindFilterUpdateCallbacksToCurrentContext() {
        this.projectColumnFilterOptionsUpdateCallback = this.updateProjectColumnFilterOptions.bind(this);
    }

    toProjectFilterOptions(result): FilterOption[] {
        const projects: [] = result.Response.Items;
        const filteroptions: FilterOption[] = [];
        projects.forEach((item: any) => {
          const filterOption: FilterOption = {
            displayName: item.Project.Name,
            filterValue: item.Project.Id
          };
          filteroptions.push(filterOption);

        });
        return filteroptions;
    }

    protected getOverrideColumnParameters(): OverrideColumnParameters<FileSearchColumnFields> {
        const inclusiveColumns: FileSearchColumnFields[] = [
            FileSearchColumnFields.Name,
            FileSearchColumnFields.Analysis,
            FileSearchColumnFields.Size,
            FileSearchColumnFields.Created
        ];

        const overrideColumnDefinitions: OverrideColumnDefinition<FileSearchColumnFields>[] = [
            {
              colId: FileSearchColumnFields.Name,
              checkboxSelection: false
            }
        ];

        return {
            inclusiveColumns,
            overrideColumnDefinitions
        };
    }

    protected getDefaultColumnDefinitions() {
        const fileConfig = this.fileResourceConfig;

        const defaultColumnDefinitions = [
            {
                headerName: 'Name',
                field: 'DatasetFile.Name',
                cellRendererFramework: FilePreviewCellRendererComponent,
                colId: FileSearchColumnFields.Name,
                sortable: false,
                flex: 4,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'File Type',
                valueGetter: (params) => {
                    if (!params.data) { return; }
                    return getFileTypeFromName(params.data.DatasetFile.Name);
                },
                colId: FileSearchColumnFields.FileType,
                cellRenderer: 'span',
                sortable: false,
                flex: 1.5,
                minWidth: ColumnMinWidth.FILE_TYPE,
                filterActive: true,
                filterOptions: of(FILE_TYPE_FILTER_OPTIONS),
            },
            {
                headerName: 'Analysis',
                field: 'DatasetFile.ParentDataset.AppSession',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data) {
                        const appSession = params.data.DatasetFile.ParentDataset.AppSession;
                        return DataTableUtilities.getRadLinkParams(
                            appSession.Name,
                            BsApiEndPoints.getAnalysesRoute(appSession.Id)
                        );
                    } else {
                        return {};
                    }
                },
                colId: FileSearchColumnFields.Analysis,
                sortable: false,
                flex: 3,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'Dataset',
                field: 'DatasetFile.ParentDataset',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data) {
                        const appSession = params.data.DatasetFile.ParentDataset.AppSession;
                        const dataset = params.data.DatasetFile.ParentDataset;
                        return DataTableUtilities.getRadLinkParams(
                            appSession.Name,
                            BsApiEndPoints.getAppResultRoute(appSession.Id, dataset.Id)
                        );
                    } else {
                        return {};
                    }
                },
                colId: FileSearchColumnFields.Dataset,
                sortable: false,
                flex: 3,
                minWidth: ColumnMinWidth.RESOURCE
            },
            {
                headerName: 'Project',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if (params.data && params.data.DatasetFile && params.data.DatasetFile.ParentProject) {
                        const project = params.data.DatasetFile.ParentProject;
                        return DataTableUtilities.getRadLinkParams(
                            project.Name,
                            BsApiEndPoints.getProjectDetailsRoute(project.Id)
                        );
                    }
                    return {};
                },
                colId: FileSearchColumnFields.Project,
                sortable: false,
                flex: 3,
                minWidth: ColumnMinWidth.RESOURCE,
                filterActive: true,
                filterWithSearchBox: true,
                filterOptions: this.projectFilterOptions$,
                updateFilterOptionsCallback: this.projectColumnFilterOptionsUpdateCallback,
            },
            {
                headerName: 'Size',
                valueGetter: (params) => {
                    if (!params.data) { return; }
                    return BytesFilter(params.data.DatasetFile.Size);
                  },
                colId: FileSearchColumnFields.Size,
                cellRenderer: 'span',
                sortable: false,
                flex: 2,
                minWidth: ColumnMinWidth.SIZE
            },
            {
                headerName: 'Created',
                valueGetter: (params) => {
                    if (!params.data) { return; }
                    return this.bsshDatePipe.transform(params.data.DatasetFile.DateCreated);
                  },
                filterActive: false,
                sortable: false,
                colId: FileSearchColumnFields.Created,
                cellRenderer: 'span',
                flex: 1.5,
                minWidth: ColumnMinWidth.DATE
              },
            {
                headerName: 'Path',
                colId: FileSearchColumnFields.Path,
                sortable: false,
                flex: 4,
                minWidth: ColumnMinWidth.RESOURCE,
                cellRendererSelector: (params) => {
                    if (params.data) {
                        const resource = this.resourceDictionaryService.getSearchResultResource(params.data, this.resourceType) as IResource & { Path?: string };
                        return DataTableUtilities.getSpanRenderer(StringUtilities.getDirectoryPath(resource.Path, resource.Name));
                    } else {
                        return DataTableUtilities.getSpanRenderer();
                    }
                },
            },
            {
                headerName: 'Dataset Status',
                colId: FileSearchColumnFields.DatasetStatus,
                valueGetter: params => {
                    if (!params || !params.data) { return; }
                    const value = _.get(params.data, 'DatasetFile.ParentDataset.QcStatus', null) as string;
                    return !value || value.toLowerCase() === 'undefined' ? '--' : this.statusToDisplayName.transform(value);
                },
                cellStyle: (params) => {
                    if (!params || !params.data) { return; }
                    const value = _.get(params.data, 'DatasetFile.ParentDataset.QcStatus', null) as string;
                    return value === FastqQcStatus.QC_FAILED ? { fontWeight: 'bold' } : null;
                },
                cellRenderer: 'span',
                flex: 1.5,
                minWidth: 160,
                resizable: true,
            },
        ];
        return defaultColumnDefinitions;
    }
}
